import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../hooks';
import { getOnboardingUserIdFromLocalStorage } from '../../../helper';
import {
  getOnboarding,
  getOnboardingState,
} from '../../../store/V2/OnboardingSlice';
import { getCurriculumsV2 } from '../../../store/V2/CurriculumSlice';

import FullScreenLoader from '../../../components/Loaders/FullScreenloader';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';

const stepsLength = 6;
const stepComponents = [Step1, Step2, Step3, Step4, Step5, Step6];

const Onboarding: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const { onboardingData } = useAppSelector(getOnboardingState);
  const onboardingUserId = getOnboardingUserIdFromLocalStorage();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  // Convert step number to 0-based index for array access
  let currentStep = parseInt(stepParam || '1', 10) - 1;
  if (
    Number.isNaN(currentStep) ||
    currentStep < 0 ||
    currentStep >= stepsLength
  ) {
    currentStep = 0;
  }
  const StepComponent = stepComponents[currentStep];

  const next = () => {
    const nextStep = Math.min(currentStep + 1, stepsLength - 1);
    // Convert back to 1-based step number for URL
    setSearchParams({ step: String(nextStep + 1) });
  };
  const back = () => {
    const prevStep = Math.max(currentStep - 1, 0);
    // Convert back to 1-based step number for URL
    setSearchParams({ step: String(prevStep + 1) });
  };

  const getNextStepBasedOnData = () => {
    if (!onboardingData) return 1;

    const {
      curriculum,
      subject,
      grade,
      level,
      hasTutoredBefore,
      learningStyle,
    } = onboardingData;

    const hasTutoredBeforeValue =
      hasTutoredBefore === false || hasTutoredBefore === true;

    if (
      curriculum &&
      subject &&
      grade &&
      level &&
      hasTutoredBeforeValue &&
      learningStyle
    ) {
      return 6;
    }
    if (curriculum && subject && grade && level && hasTutoredBeforeValue) {
      return 5;
    }
    if (curriculum && subject && grade && level) {
      return 4;
    }
    if (curriculum && subject && grade) {
      return 3;
    }
    if (curriculum && subject) {
      return 2;
    }
    return 1;
  };

  useEffect(() => {
    dispatch(getCurriculumsV2());

    if (!isInitialLoading && onboardingUserId) {
      setIsLoading(true);
      dispatch(getOnboarding(onboardingUserId));
    }
  }, []);

  useEffect(() => {
    if (!isInitialLoading && onboardingData && onboardingData._id) {
      setIsLoading(false);
      setIsInitialLoading(true);

      if (!stepParam) {
        const nextStep = getNextStepBasedOnData();
        setSearchParams({ step: String(nextStep) });
      }
    }
  }, [onboardingData]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <StepComponent onNext={next} onBack={back} currentStep={currentStep} />
  );
};

export default Onboarding;
